// Resolves the full set of ixora_user_id values a supervisor has ever used,
// so a single Firestore query can union historical + current feedback.
//
// Background: post-IGP merger (2026-05-18) and the legacy-supervisor auth
// migration (2026-05-25, migrate-legacy-supervisors.mjs), a supervisor's
// canonical ixora_user_id changed from a legacy code (e.g. "EM3-x") to the
// IxoraOne employeeId (e.g. "ICS001234"). Old feedback_collection docs still
// reference the legacy id in feedback_collector_ixora_userID; new docs use
// the canonical id. The union here lets one query find both.
//
// The list is sourced from the supervisor doc:
//   - ixora_user_id           → current canonical
//   - legacyIxoraUserIds[]    → any prior values, written by the backfill
//                                script and migrate-legacy-supervisors.mjs

import { query, where } from 'firebase/firestore';

const FIRESTORE_IN_CAP = 30; // Firestore `in` operator hard limit.

/**
 * Returns a deduped array of ixora_user_id strings for a supervisor doc.
 * Falsy values (null, '', undefined) are stripped.
 */
export function resolveSupervisorIxoraIds(supervisor) {
  if (!supervisor) return [];
  const current = supervisor.ixora_user_id;
  const legacy = Array.isArray(supervisor.legacyIxoraUserIds)
    ? supervisor.legacyIxoraUserIds
    : [];
  const merged = [current, ...legacy].filter(
    (v) => typeof v === 'string' && v.length > 0,
  );
  // Dedup while preserving order (current id first).
  const seen = new Set();
  const out = [];
  for (const v of merged) {
    if (seen.has(v)) continue;
    seen.add(v);
    out.push(v);
    if (out.length >= FIRESTORE_IN_CAP) break;
  }
  return out;
}

/**
 * Builds a Firestore `where` clause on feedback_collector_ixora_userID that
 * matches any of the supervisor's known ids. Returns null when no ids are
 * available (caller should short-circuit and skip the query — running with
 * an empty `in` array throws at the SDK level).
 *
 * Single-id supervisors use `==` to avoid the `in` cost; multi-id supervisors
 * use `in`.
 */
export function whereFeedbackByAnySupervisorId(supervisor) {
  const ids = resolveSupervisorIxoraIds(supervisor);
  if (ids.length === 0) return null;
  if (ids.length === 1) {
    return where('feedback_collector_ixora_userID', '==', ids[0]);
  }
  return where('feedback_collector_ixora_userID', 'in', ids);
}

/**
 * Convenience: wrap an existing base query with the supervisor id union.
 * Returns null when the supervisor has no resolvable ids (caller skips).
 */
export function withSupervisorIdFilter(baseQuery, supervisor) {
  const clause = whereFeedbackByAnySupervisorId(supervisor);
  if (!clause) return null;
  return query(baseQuery, clause);
}
